/**
 * Agora Real-Time STT (Speech-to-Text) Service
 *
 * Uses Agora's Real-Time STT REST API to transcribe audio in a meeting channel.
 * The STT agent joins the channel, subscribes to audio streams, and publishes
 * transcription results as data stream messages (protobuf-encoded).
 *
 * Client receives transcriptions via the Agora RTC client's "stream-message" event.
 */

import protobuf from 'protobufjs';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BASE_URL || '';

// ==================== PROTOBUF DEFINITION ====================
// Based on Agora's SttMessage.proto schema

const PROTO_DEFINITION = `
syntax = "proto3";

package Agora.SpeechToText;

message Text {
  reserved 1 to 3, 5, 7 to 9, 11, 17;
  int64 uid = 4;
  int64 time = 6;
  repeated Word words = 10;
  int32 duration_ms = 12;
  string data_type = 13;
  repeated Translation trans = 14;
  string culture = 15;
  int64 text_ts = 16;
  OriginalTranscript original_transcript = 18;
}

message Word {
  reserved 2, 3, 5;
  string text = 1;
  bool is_final = 4;
}

message Translation {
  bool is_final = 1;
  string lang = 2;
  repeated string texts = 3;
}

message OriginalTranscript {
  string culture = 1;
  repeated Word words = 2;
}
`;

// Parse the proto definition once at module load
let TextMessage = null;
let protoLoaded = false;

function loadProto() {
  if (protoLoaded) return TextMessage;
  try {
    const root = protobuf.parse(PROTO_DEFINITION, { keepCase: true }).root;
    TextMessage = root.lookupType('Agora.SpeechToText.Text');
    protoLoaded = true;
    console.log('[Agora STT] Protobuf schema loaded successfully');
    return TextMessage;
  } catch (err) {
    console.error('[Agora STT] Failed to load protobuf schema:', err);
    return null;
  }
}

// Initialize on import
loadProto();

// ==================== PROTOBUF PARSER ====================

/**
 * Parse a protobuf-encoded STT data stream message
 * @param {Uint8Array} data - Raw binary data from stream-message event
 * @returns {{ text: string, isFinal: boolean, speakerUid: number, culture: string, dataType: string, translations: Array }} parsed result
 */
export function parseSttMessage(data) {
  const MessageType = loadProto();
  if (!MessageType) {
    console.warn('[Agora STT] Protobuf not loaded, cannot parse message');
    return null;
  }

  try {
    const decoded = MessageType.decode(new Uint8Array(data));
    const dataType = decoded.data_type || 'transcribe';

    // Extract transcription text from words array
    const words = decoded.words || [];
    const text = words.map(w => w.text).join('');
    const isFinal = words.length > 0 ? words[words.length - 1].is_final : false;

    // Extract translations if available
    const translations = (decoded.trans || []).map(t => ({
      lang: t.lang,
      text: (t.texts || []).join(''),
      isFinal: t.is_final,
    }));

    // For translate messages, also extract original transcript if available
    let originalText = '';
    if (decoded.original_transcript && decoded.original_transcript.words) {
      originalText = decoded.original_transcript.words.map(w => w.text).join('');
    }

    return {
      text,
      isFinal,
      speakerUid: Number(decoded.uid) || 0,
      culture: decoded.culture || '',
      dataType,
      durationMs: decoded.duration_ms || 0,
      timestamp: Number(decoded.text_ts) || Date.now(),
      translations,
      originalText,
    };
  } catch (err) {
    console.warn('[Agora STT] Failed to parse protobuf message:', err);
    return null;
  }
}

// ==================== REST API CALLS ====================

/**
 * Start STT for a meeting channel
 * @param {string} channelName - The Agora channel name (meetingId)
 * @param {string[]} languages - Languages to transcribe (e.g., ['en-US'])
 * @param {object} [options] - Additional options
 * @param {object} [options.translateConfig] - Agora translation config { languages: [{ source, target }] }
 * @param {boolean} [options.forceRestart] - Force restart even if already running (for language changes)
 * @returns {{ agentId: string, pubBotUid: string }}
 */
export async function startStt(channelName, languages = ['en-US'], options = {}) {
  const body = {
    channelName,
    languages,
  };
  if (options.translateConfig) {
    body.translateConfig = options.translateConfig;
  }
  if (options.forceRestart) {
    body.forceRestart = true;
  }
  const response = await axios.post(`${API_BASE}/agora/stt/start`, body);
  return response.data;
}

/**
 * Stop STT for a meeting channel
 * @param {string} channelName
 */
export async function stopStt(channelName) {
  const response = await axios.post(`${API_BASE}/agora/stt/stop`, {
    channelName,
  });
  return response.data;
}

/**
 * Query STT status for a meeting channel
 * @param {string} channelName
 * @returns {{ active: boolean, agentId?: string, pubBotUid?: string, status?: string }}
 */
export async function querySttStatus(channelName) {
  const response = await axios.get(`${API_BASE}/agora/stt/status/${channelName}`);
  return response.data;
}

// ==================== LANGUAGE MAPPING ====================

// Map our internal language IDs to Agora STT BCP-47 codes
const LANGUAGE_MAP = {
  'en': 'en-US',
  'es': 'es-ES',
  'fr': 'fr-FR',
  'de': 'de-DE',
  'pt-br': 'pt-BR',
  'it': 'it-IT',
  'zh-cn': 'zh-CN',
  'zh-tw': 'zh-TW',
  'ja': 'ja-JP',
  'ko': 'ko-KR',
  'ar': 'ar-SA',
  'ru': 'ru-RU',
  'tr': 'tr-TR',
  'nl': 'nl-NL',
  'pl': 'pl-PL',
  'hi': 'hi-IN',
  'vi': 'vi-VN',
  'id': 'id-ID',
  'th': 'th-TH',
  'uk': 'uk-UA',
  'hu': 'hu-HU',
  'ro': 'ro-RO',
};

// Languages supported by Agora STT for translation.
// See: https://docs.agora.io/en/real-time-stt/develop/translation
// Source = can be spoken and transcribed for translation
// Target = can be translated INTO
export const TRANSLATION_SOURCE = new Set([
  'en', 'es', 'fr', 'de', 'it', 'zh-cn', 'zh-tw',
  'ja', 'ko', 'ar', 'ru', 'tr', 'nl', 'hi', 'vi',
  'id', 'th',
  // pt-BR is NOT a source (only pt-PT is), but we map pt-br -> pt-BR
  // pl, uk, hu, ro, pa are NOT translation sources
]);
export const TRANSLATION_TARGET = new Set([
  'en', 'es', 'fr', 'de', 'pt-br', 'it', 'zh-cn', 'zh-tw',
  'ja', 'ko', 'ar', 'ru', 'tr', 'nl', 'pl', 'hi', 'vi',
  'id', 'th', 'uk', 'hu', 'ro',
  // pa (Punjabi) is NOT a translation target — uses Google Translate fallback
]);

/**
 * Convert internal language ID to Agora STT BCP-47 code
 */
export function toAgoraSttLanguage(langId) {
  return LANGUAGE_MAP[langId] || 'en-US';
}

/**
 * Check if a language pair supports Agora STT translation
 * @param {string} sourceLangId - The spoken language id
 * @param {string} targetLangId - The caption/target language id
 * @returns {boolean}
 */
export function isTranslationSupported(sourceLangId, targetLangId) {
  return TRANSLATION_SOURCE.has(sourceLangId) && TRANSLATION_TARGET.has(targetLangId);
}

/**
 * Build translateConfig with up to 10 target languages for the given source.
 * Agora limits to 10 targets per source language — we prioritize:
 *   1. The user's selected captionLanguage (always included)
 *   2. Additional priority languages passed in (other participants' selections)
 *   3. Fill remaining slots with other supported languages
 *
 * @param {string} sourceLangId - The spoken language id (e.g. 'en')
 * @param {string|string[]} [priorityLangIds] - Language id(s) to prioritize (e.g. 'es' or ['es','fr'])
 * @returns {object|null} translateConfig or null if source doesn't support translation
 */
export function buildAllTargetsTranslateConfig(sourceLangId, priorityLangIds) {
  if (!TRANSLATION_SOURCE.has(sourceLangId)) return null;
  const sourceCode = toAgoraSttLanguage(sourceLangId);

  // Normalize priority languages to an array
  const priorities = Array.isArray(priorityLangIds)
    ? priorityLangIds
    : priorityLangIds ? [priorityLangIds] : [];

  // Build ordered target list: priority languages first, then fill remaining slots
  const targetCodes = [];
  const addedCodes = new Set();

  // 1. Add priority languages first (if they're valid targets and not the source)
  for (const langId of priorities) {
    if (langId && langId !== sourceLangId && TRANSLATION_TARGET.has(langId)) {
      const code = toAgoraSttLanguage(langId);
      if (!addedCodes.has(code)) {
        targetCodes.push(code);
        addedCodes.add(code);
      }
    }
  }

  // 2. Fill remaining slots with other supported targets
  for (const targetId of TRANSLATION_TARGET) {
    if (targetCodes.length >= 10) break;
    if (targetId === sourceLangId) continue;
    const code = toAgoraSttLanguage(targetId);
    if (!addedCodes.has(code)) {
      targetCodes.push(code);
      addedCodes.add(code);
    }
  }

  return {
    languages: [{ source: sourceCode, target: targetCodes.slice(0, 10) }]
  };
}
